import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { FeaturesStreamsService } from 'src/app/shared/common/current-features/features-streams.service';
import { TaskFeaturesStreamsService } from 'src/app/shared/common/current-features/task-features-streams.service';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { TasksStore } from 'src/app/shared/common/current-tasks/tasks-store.service';
import { TasksStreamsService } from 'src/app/shared/common/current-tasks/tasks-streams.service';
import { CurrentUserStreamService } from 'src/app/shared/common/current-user/current-user-stream.service';
import { Task, TaskCreationMode, TaskPanelTabs, TaskStatus } from 'src/app/shared/common/task/task';

import { MapMenuCode } from 'src/app/feature/map-viewer/map-menus/map-menu-list';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { CustomScrollbarComponent } from 'src/app/shared/common/components/custom-scrollbar.component';
import { NotificationType } from 'src/app/shared/common/components/gsp-notification/gsp-notification.component';
import {
    SortChangeEvent,
    SortMenuComponent,
    SortMenuItem
} from 'src/app/shared/common/components/sort-menu/sort-menu.component';
import { MenuService } from 'src/app/shared/common/layout/menu.service';
import { ItemSortMode, ItemSortService } from 'src/app/shared/common/services/item-sort.service';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { UserRole } from 'src/app/shared/user/user';

export interface PanelTask extends Task {
    loading?: boolean;
}

@Component({
    templateUrl: './task-panel.component.html',
    selector: 'task-panel'
})
export class TaskPanelComponent implements OnInit, OnDestroy {
    @Input()
    isAddToTaskMode: boolean = false;

    @ViewChild(CustomScrollbarComponent, { static: false })
    customScrollbar: CustomScrollbarComponent;

    @ViewChild('taskSortMenu') taskSortMenu: SortMenuComponent<PanelTask>;

    tasks: PanelTask[] = [];
    activeTasks: PanelTask[] = [];
    closedTasks: PanelTask[] = [];
    loading: boolean;
    currentTab: TaskPanelTabs;
    showDataLayerTitle: string;
    newFeaturesForTasks: Feature[];
    taskSearchControl = new UntypedFormControl();
    isTaskSearchActive = false;
    isTaskVisibilityRestricted = false;
    isSaveEnabled = false;
    isTasksFiltered = false;
    isNonTemplatedFeaturesExcluded = false;
    selectedTaskId: string;
    taskCreationMode: TaskCreationMode;

    currentScrollPosition: number;

    // Expose enum to template
    NotificationType = NotificationType;
    TaskPanelTabs = TaskPanelTabs;
    ButtonType = ButtonType;
    TaskCreationMode = TaskCreationMode;

    // For addToTask mode
    public taskToCheckboxMap: Map<Task, boolean> = new Map<Task, boolean>();

    private destroyed = new Subject<void>();

    // #region Sort Menu related properties
    taskSortMode: ItemSortMode = ItemSortMode.DATE_MODIFIED;
    taskSortDirection: 'asc' | 'desc' = 'desc';
    scrollToTop: boolean;
    // Sort menu items list
    public sortMenuItems: SortMenuItem<PanelTask>[];
    // #endregion

    constructor(
        private tasksStore: TasksStore,
        private sortService: ItemSortService,
        private tasksStreams: TasksStreamsService,
        private taskFeaturesStreams: TaskFeaturesStreamsService,
        private currentUserStream: CurrentUserStreamService,
        private projectStream: ProjectStreamService,
        private messaging: MessagingService,
        private featuresStreams: FeaturesStreamsService,
        private translate: TranslationService,
        private menuService: MenuService
    ) {}

    ngOnInit(): void {
        this.tasks = [];
        this.activeTasks = [];
        this.loading = true;
        this.currentTab = TaskPanelTabs.ACTIVE;

        this.sortMenuItems = this.sortService.getSortMenuItems<PanelTask>();

        this.initialize();
    }

    ngOnDestroy(): void {
        this.tasksStreams.disableMapviewerStream.next(false);
        this.tasksStreams.setSelectedTasks([]);
        this.destroyed.next(null);
    }

    private initialize(): void {
        this.tasksStreams.disableMapviewerStream.next(true); // Disable other app capabilities when task panel is open

        this.tasksStore.mapWorkspaceTasksLoadingStream.pipe(takeUntil(this.destroyed)).subscribe(loadingIds => {
            this.loading = this.loading = !!loadingIds.length;
        });

        this.tasksStreams.pendingLoadingTaskStream.pipe(takeUntil(this.destroyed)).subscribe(pendingTask => {
            this.activeTasks.forEach((task, index) => {
                if (pendingTask.task.id === task.id) {
                    this.activeTasks[index].loading = pendingTask.loading;
                }
            });
            this.closedTasks.forEach((task, index) => {
                if (pendingTask.task.id === task.id) {
                    this.closedTasks[index].loading = pendingTask.loading;
                }
            });
        });

        this.tasksStore.mapWorkspaceTasksStream.pipe(takeUntil(this.destroyed)).subscribe(tasks => {
            this.tasks = [];
            this.activeTasks = [];
            this.closedTasks = [];

            if (tasks) {
                this.tasks = tasks;
                this.activeTasks = this.tasks.filter(task => task.status !== TaskStatus.CLOSED);
                this.closedTasks = this.tasks.filter(task => task.status === TaskStatus.CLOSED);

                const visibleTasks = this.tasksStreams.visibleTasksStream.getValue();
                if (this.activeTasks && visibleTasks) {
                    this.activeTasks.forEach(tsk => {
                        tsk.visible = visibleTasks.some(visibleTask => visibleTask.id === tsk.id);
                    });
                }

                this.tasks.forEach(task => {
                    task.totalTaskedForms = this.tasksStore.totalTaskedForms(task);
                    if (task.visible && !task.isClosed) {
                        this.tasksStreams.deferVisibleTasksStream.next([task]);
                    }
                });
            }
        });

        this.tasksStreams.selectedTasksStream.pipe(takeUntil(this.destroyed)).subscribe(selectedTasks => {
            this.selectedTaskId = selectedTasks.length > 0 ? selectedTasks[0].id : null;
            this.taskCreationMode = null;
        });

        this.taskSearchControl.valueChanges
            .pipe(takeUntil(this.destroyed), debounceTime(700))
            .subscribe((taskSearchInput: string) => {
                this.loadTasksBySearch(taskSearchInput, true);
            });

        this.updateTaskVisibility();

        if (this.isAddToTaskMode) {
            this.setupAddToTask();
        }

        if (this.taskFeaturesStreams.taskSelectedFeaturesStream.getValue().length) {
            this.taskCreationMode = TaskCreationMode.NEW_FROM_SELECTION;
        }
    }

    private updateTaskVisibility() {
        this.projectStream.getUserWithRolesForCurrentProject(this.currentUserStream.currentUser.id).then(async user => {
            if (user.role === UserRole.USER) {
                const projectSettings = this.projectStream.getCurrentProject().settings;
                this.isTaskVisibilityRestricted = projectSettings.isTodosRestricted;
            }
        });
    }

    // #region Sort Menu related methods
    /**
     * Handles task sorting based on the selected sort option and direction
     * @param currentSort The sort change event from the sort menu
     */
    public sortTaskList(currentSort: SortChangeEvent<PanelTask>): void {
        const numericSortId = Number(currentSort.sortMode.id);
        const isSameMode = this.taskSortMode === numericSortId;

        // Reset scroll position after sorting
        setTimeout(() => (this.scrollToTop = false));

        if (isSameMode) {
            // Reverse lists if the mode hasn't changed.
            this.activeTasks = [...this.activeTasks].reverse();
            this.closedTasks = [...this.closedTasks].reverse();
        } else {
            this.activeTasks = this.sortService.sortItems<PanelTask>(this.activeTasks, currentSort);
            this.closedTasks = this.sortService.sortItems<PanelTask>(this.closedTasks, currentSort);
        }

        this.taskSortMode = numericSortId;
        this.taskSortDirection = currentSort.direction;
        this.scrollToTop = true;
    }
    // #endregion

    public isSearchButtonDisabled(): boolean {
        const isTabEmpty =
            (this.currentTab === TaskPanelTabs.ACTIVE && this.activeTasks.length === 0 && !this.isTaskSearchActive) ||
            (this.currentTab === TaskPanelTabs.CLOSED && this.closedTasks.length === 0 && !this.isTaskSearchActive);

        return this.loading || isTabEmpty;
    }

    public toggleTaskSearch(): void {
        this.isTaskSearchActive = !this.isTaskSearchActive;
        if (!this.isTaskSearchActive) {
            this.taskSearchControl.setValue('', { emitEvent: false });
            this.setCurrentTab(this.currentTab, true);
        }
    }

    private async loadTasksBySearch(taskSearchInput: string = '', fullTaskRefresh?: boolean): Promise<void> {
        const currentTaskStatus = this.currentTab;

        await this.tasksStore.loadCurrentWorkspaceTasksBySearch(currentTaskStatus, taskSearchInput);

        if (fullTaskRefresh) {
            await this.tasksStore.loadCurrentWorkspaceTasksBySearch(this.getInactiveTab(), taskSearchInput);
        }
    }

    public async setCurrentTab(tab: TaskPanelTabs, forceTabRefresh: boolean = false): Promise<void> {
        this.currentTab = tab;

        if (forceTabRefresh) {
            this.loadTasksBySearch('', forceTabRefresh);
        }

        if (this.taskSearchControl.value) {
            this.loadTasksBySearch(this.taskSearchControl.value);
        }
    }

    public toggleVisibleTask(task: PanelTask, event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        if (!task.loading) {
            task.visible = !task.visible;

            this.tasksStreams.setVisibleTasks(this.tasks.filter(x => x.visible));

            if (task.visible) {
                this.tasksStreams.deferVisibleTasksStream.next([task]);
            } else {
                this.tasksStreams.deferHideTasksStream.next([task]);
            }
        }
    }

    public setSelectedTask(task: PanelTask): void {
        if (this.isAddToTaskMode) {
            return;
        }

        this.tasksStreams.setSelectedTasks([]);
        this.tasks.forEach(otherTask => {
            otherTask.selected = task.id === otherTask.id;
        });

        if (task.selected) {
            const taskFeatureIds = task.features.map(feature => feature.featureId);
            this.featuresStreams.replaceOnSelectedFeatures(taskFeatureIds);
        }
        this.tasksStreams.setSelectedTasks(this.tasks.filter(x => x.selected));

        this.currentScrollPosition = this.customScrollbar.scrollPosition.getValue();
        this.selectedTaskId = task.id;
    }

    public closePanel(): void {
        this.selectedTaskId = null;
        this.menuService.openPreviousActiveMenus();
        this.tasksStreams.setSelectedTasks([]);
        this.tasksStreams.disableMapviewerStream.next(false);
    }

    // Add form features to existing task workflow

    public setTaskToCheckboxMap(task: Task, $event: Event) {
        this.taskToCheckboxMap.set(task, ($event.target as HTMLInputElement).checked);
        this.isSaveEnabled = Array.from(this.taskToCheckboxMap.values()).some(value => value === true);
    }

    private setupAddToTask() {
        const selectedFeatures = this.featuresStreams.selectedFeaturesStream.getValue();
        this.newFeaturesForTasks = this.tasksStreams.addFeaturesToTaskStream.getValue();

        if (selectedFeatures.length !== this.newFeaturesForTasks.length) {
            this.isNonTemplatedFeaturesExcluded = true;
        }

        // Close details panel if it is open while adding features to task from feature-panel
        this.selectedTaskId = null;

        this.activeTasks = this.tasks.filter(
            task =>
                !task.features.some(taskFeature =>
                    this.newFeaturesForTasks.some(newFeature => newFeature.id === taskFeature.formId)
                )
        );
        if (this.activeTasks.length !== this.tasks.length) {
            this.isTasksFiltered = true;
        }

        this.activeTasks.forEach(task => {
            if (!this.taskToCheckboxMap.has(task)) {
                this.taskToCheckboxMap.set(task, false);
            }
        });
    }

    public saveAddToTask(): void {
        const selectedTasks = Array.from(this.taskToCheckboxMap.entries())
            .filter(([key, value]) => value)
            .map(([key, value]) => key);

        const addedTaskFeatures = this.tasksStore.getTaskFeaturesFromSelectedFeatures(this.newFeaturesForTasks);
        addedTaskFeatures.forEach(task => (task.formId = task.featureId));

        this.loading = true;

        const saveTaskPromises = selectedTasks.map(task => {
            const updatedTask = { ...task, features: [...task.features, ...addedTaskFeatures] };
            return this.tasksStore
                .saveTask(updatedTask)
                .then(savedTask => {
                    const toastr = this.messaging.showSuccessWithAction(
                        this.translate.instant('TC.TodosTask.TaskFormsAdded', { taskName: savedTask.title }),
                        null,
                        this.translate.instant('Common.Open').toUpperCase(),
                        { enableHtml: true }
                    );
                    toastr.onTap.subscribe(() => {
                        this.tasksStreams.selectedTasksStream.next([savedTask]);
                        this.menuService.setActiveMenus([MapMenuCode.TASKS]);
                    });

                    this.closePanel();
                    return savedTask;
                })
                .catch(error => {
                    this.messaging.showError(this.translate.instant('TC.Common.ErrorWhileUpdating'));
                    throw error;
                });
        });

        Promise.all(saveTaskPromises).finally(() => {
            this.loading = false;
        });
    }

    private getInactiveTab(): TaskPanelTabs {
        return this.currentTab === TaskPanelTabs.ACTIVE ? TaskPanelTabs.CLOSED : TaskPanelTabs.ACTIVE;
    }
}
